import { cn, Skeleton } from '@up2stay/up2promo-ui';
import type { ReactNode } from 'react';

/**
 * Density-aware card grid for Status Board resource lists.
 * Low counts stay intentional (not stranded on a wide canvas);
 * 3+ uses auto-fill so equal-height cards share column width.
 */
export function resourceCardGridClass(slotCount: number): string {
  if (slotCount <= 1) {
    return 'grid max-w-md grid-cols-1 items-stretch gap-3';
  }
  if (slotCount === 2) {
    return 'grid max-w-2xl grid-cols-1 items-stretch gap-3 sm:grid-cols-2';
  }
  return 'grid grid-cols-[repeat(auto-fill,minmax(min(100%,17.5rem),1fr))] items-stretch gap-3';
}

/** Fixed breakpoint columns — prefer adaptive unless a page needs a rigid board. */
export const RESOURCE_CARD_GRID_COLUMNS_CLASS =
  'grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 lg:grid-cols-3';

export type ResourceCardGridProps = {
  children: ReactNode;
  /** Total slots including add-card; drives adaptive density. Ignored when density="columns". */
  slotCount?: number;
  /**
   * `adaptive` (default): Locations-style density from slotCount.
   * `columns`: sm/lg breakpoint columns (legacy teams/templates board).
   */
  density?: 'adaptive' | 'columns';
  className?: string;
  /** When true, wraps as role="list" (children should be ResourceCardGridItem). */
  asList?: boolean;
  'aria-busy'?: boolean;
};

export function ResourceCardGrid({
  children,
  slotCount = 3,
  density = 'adaptive',
  className,
  asList = true,
  'aria-busy': ariaBusy,
}: ResourceCardGridProps) {
  const gridClass =
    density === 'columns'
      ? RESOURCE_CARD_GRID_COLUMNS_CLASS
      : resourceCardGridClass(slotCount);

  return (
    <div
      className={cn(gridClass, className)}
      role={asList ? 'list' : undefined}
      aria-busy={ariaBusy}
    >
      {children}
    </div>
  );
}

export type ResourceCardGridItemProps = {
  children: ReactNode;
  className?: string;
};

/** Equal-height stretch slot for cards inside ResourceCardGrid. */
export function ResourceCardGridItem({
  children,
  className,
}: ResourceCardGridItemProps) {
  return (
    <div role="listitem" className={cn('h-full min-h-0', className)}>
      {children}
    </div>
  );
}

export type ResourceCardGridSkeletonProps = {
  count?: number;
  /** Approximate card height for skeleton blocks. */
  className?: string;
  density?: 'adaptive' | 'columns';
  /** Used when density="adaptive". Defaults to count. */
  slotCount?: number;
};

export function ResourceCardGridSkeleton({
  count = 3,
  className,
  density = 'adaptive',
  slotCount,
}: ResourceCardGridSkeletonProps) {
  return (
    <ResourceCardGrid
      density={density}
      slotCount={slotCount ?? count}
      asList={false}
      aria-busy
    >
      {Array.from({ length: count }).map((_, index) => (
        <Skeleton
          key={index}
          className={cn('h-32 w-full rounded-lg', className)}
        />
      ))}
    </ResourceCardGrid>
  );
}
